/** @format */

import axios from "axios";
import { API_BASE_URL } from "../../axiosConfig/apiUrl";

// Using global axios to inherit auth headers from axiosInterceptor for mobile browser support

// ==================== Types ====================

export interface RetryPayoutByDateParams {
	date: string; // YYYY-MM-DD format
}

export interface RetrySpecificPayoutParams {
	cycleId: string;
	payoutIndex: number;
}

export interface CirclePayoutDetailsParams {
	circleId: string;
	selectedDate?: string; // YYYY-MM-DD format
	dateRangeStart?: string; // YYYY-MM-DD format
	dateRangeEnd?: string; // YYYY-MM-DD format
}

// ==================== Payout Overview Service Methods ====================

/**
 * Get complete payout overview (past, present, future payouts with statistics)
 */
export const fetchPayoutOverviewAsAdmin = async () => {
	try {
		const response = await axios.get(`${API_BASE_URL}/admin/payouts/overview`, {
			withCredentials: true,
		});
		return response.data;
	} catch (error: any) {
		console.error("Get payout overview error:", error);
		if (error.response && error.response.data) {
			throw error.response.data;
		} else {
			throw new Error(
				error.message || "Unknown Error Occurred While Making Request",
			);
		}
	}
};

/**
 * Get all retryable payouts (missed and failed)
 */
export const fetchRetryablePayoutsAsAdmin = async () => {
	try {
		const response = await axios.get(
			`${API_BASE_URL}/admin/payouts/retryable`,
			{
				withCredentials: true,
			},
		);
		return response.data;
	} catch (error: any) {
		console.error("Get retryable payouts error:", error);
		if (error.response && error.response.data) {
			throw error.response.data;
		} else {
			throw new Error(
				error.message || "Unknown Error Occurred While Making Request",
			);
		}
	}
};

// ==================== Payout Retry Service Methods ====================

/**
 * Retry all payouts for a specific date (YYYY-MM-DD)
 */
export const retryPayoutsForDateAsAdmin = async (
	params: RetryPayoutByDateParams,
) => {
	try {
		const response = await axios.post(
			`${API_BASE_URL}/admin/payouts/retry/date/${params.date}`,
			{},
			{
				withCredentials: true,
			},
		);
		return response.data;
	} catch (error: any) {
		console.error("Retry payouts for date error:", error);
		if (error.response && error.response.data) {
			throw error.response.data;
		} else {
			throw new Error(
				error.message || "Unknown Error Occurred While Making Request",
			);
		}
	}
};

/**
 * Retry a specific payout by cycle ID and payout index
 */
export const retrySpecificPayoutAsAdmin = async (
	params: RetrySpecificPayoutParams,
) => {
	try {
		const response = await axios.post(
			`${API_BASE_URL}/admin/payouts/retry/specific`,
			{
				cycleId: params.cycleId,
				payoutIndex: params.payoutIndex,
			},
			{
				withCredentials: true,
			},
		);
		return response.data;
	} catch (error: any) {
		console.error("Retry specific payout error:", error);
		if (error.response && error.response.data) {
			throw error.response.data;
		} else {
			throw new Error(
				error.message || "Unknown Error Occurred While Making Request",
			);
		}
	}
};

/**
 * Retry all missed/failed payouts
 */
export const retryAllPayoutsAsAdmin = async () => {
	try {
		const response = await axios.post(
			`${API_BASE_URL}/admin/payouts/retry/all`,
			{},
			{
				withCredentials: true,
			},
		);
		return response.data;
	} catch (error: any) {
		console.error("Retry all payouts error:", error);
		if (error.response && error.response.data) {
			throw error.response.data;
		} else {
			throw new Error(
				error.message || "Unknown Error Occurred While Making Request",
			);
		}
	}
};

// ==================== Filtered Payout Service Methods ====================

/**
 * Get payouts for a specific circle
 */
export const fetchPayoutsByCircleAsAdmin = async (circleId: string) => {
	try {
		const response = await axios.get(
			`${API_BASE_URL}/admin/payouts/circle/${circleId}`,
			{
				withCredentials: true,
			},
		);
		return response.data;
	} catch (error: any) {
		console.error("Get payouts by circle error:", error);
		if (error.response && error.response.data) {
			throw error.response.data;
		} else {
			throw new Error(
				error.message || "Unknown Error Occurred While Making Request",
			);
		}
	}
};

/**
 * Get payouts for a specific member
 */
export const fetchPayoutsByMemberAsAdmin = async (memberId: string) => {
	try {
		const response = await axios.get(
			`${API_BASE_URL}/admin/payouts/member/${memberId}`,
			{
				withCredentials: true,
			},
		);
		return response.data;
	} catch (error: any) {
		console.error("Get payouts by member error:", error);
		if (error.response && error.response.data) {
			throw error.response.data;
		} else {
			throw new Error(
				error.message || "Unknown Error Occurred While Making Request",
			);
		}
	}
};

/**
 * Get payouts for a specific cycle
 */
export const fetchPayoutsByCycleAsAdmin = async (cycleId: string) => {
	try {
		const response = await axios.get(
			`${API_BASE_URL}/admin/payouts/cycle/${cycleId}`,
			{
				withCredentials: true,
			},
		);
		return response.data;
	} catch (error: any) {
		console.error("Get payouts by cycle error:", error);
		if (error.response && error.response.data) {
			throw error.response.data;
		} else {
			throw new Error(
				error.message || "Unknown Error Occurred While Making Request",
			);
		}
	}
};

/**
 * Get detailed payout info for a specific circle with date filtering
 * Supports calendar-based filtering for admin payout modal
 */
export const fetchCirclePayoutDetailsAsAdmin = async (
	params: CirclePayoutDetailsParams,
) => {
	try {
		const queryParams = new URLSearchParams();
		if (params.selectedDate) {
			queryParams.append("selectedDate", params.selectedDate);
		}
		if (params.dateRangeStart) {
			queryParams.append("dateRangeStart", params.dateRangeStart);
		}
		if (params.dateRangeEnd) {
			queryParams.append("dateRangeEnd", params.dateRangeEnd);
		}

		const queryString = queryParams.toString();
		const url = `/admin/payouts/circle/${params.circleId}/details${queryString ? `?${queryString}` : ""}`;

		const response = await axios.get(`${API_BASE_URL}${url}`, {
			withCredentials: true,
		});
		return response.data;
	} catch (error: any) {
		console.error("Get circle payout details error:", error);
		if (error.response && error.response.data) {
			throw error.response.data;
		} else {
			throw new Error(
				error.message || "Unknown Error Occurred While Making Request",
			);
		}
	}
};
